import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import { green, red } from "@material-ui/core/colors";
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import { i18n } from "../../translate/i18n";
import { useDate } from "../../hooks/useDate";
import FormattedCurrency from "../FormattedCurrency";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  modernCard: {
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.2s ease-in-out',
    height: '100%',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-1px)',
    },
  },
  cardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2.5),
  },
  sectionTitle: {
    fontSize: '1.375rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tableContainer: {
    width: "100%",
    padding: "2px",
  },
  mobileRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  mobileCompanyName: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  statusIcon: {
    fontSize: '1.5rem',
  },
  desktopOnly: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mobileOnly: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
}));

const CompaniesList = () => {
  const classes = useStyles();
  const { list } = useCompanies();
  const { dateToClient } = useDate();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    if (user.id === 1) {
      loadCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const companyList = await list();
      setRecords(companyList);
    } catch (e) {
      console.error("Erro ao carregar empresas:", e);
    }
    setLoading(false);
  };

  const renderStatus = (status) => {
    return status === false ? i18n.t("compaies.table.no") : i18n.t("compaies.table.yes");
  };

  const renderPlan = (company) => {
    return company.planId !== null ? company.plan.name : "-";
  };

  const renderPlanValue = (company) => {
    if (company.planId !== null && company.plan.amount) {
      return <FormattedCurrency value={company.plan.amount} />;
    }
    return "-";
  };

  const rowStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { backgroundColor: "#fffead" };
      }
      if (diff <= 0) {
        return { backgroundColor: "#fa8c8c" };
      }
    }
    return {};
  };
  
  const cellStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { color: "#000" };
      }
      if (diff <= 0) {
        return { color: "#fff" };
      }
    }
    return {};
  };

  if (user.id !== 1) {
    return null;
  }

  return (
    <Box className={classes.section}>
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>
          Empresas
        </Typography>
      </Box>
      
      <Paper className={classes.modernCard}>
        <div className={classes.cardContent}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Desktop View */}
              <div className={`${classes.tableContainer} ${classes.desktopOnly}`}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">ID</TableCell>
                      <TableCell align="center">{i18n.t("compaies.table.name")}</TableCell>
                      <TableCell align="center">{i18n.t("compaies.table.email")}</TableCell>
                      <TableCell align="center">{i18n.t("compaies.table.plan")}</TableCell>
                      <TableCell align="center">{i18n.t("compaies.table.value")}</TableCell>
                      <TableCell align="center">{i18n.t("compaies.table.active")}</TableCell>
                      <TableCell align="center">{i18n.t("compaies.table.dueDate")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.map((company) => (
                      <TableRow style={rowStyle(company)} key={company.id}>
                        <TableCell style={cellStyle(company)} align="center">{company.id}</TableCell>
                        <TableCell style={cellStyle(company)} align="center">{company.name}</TableCell>
                        <TableCell style={cellStyle(company)} align="center">{company.email}</TableCell>
                        <TableCell style={cellStyle(company)} align="center">{renderPlan(company)}</TableCell>
                        <TableCell style={cellStyle(company)} align="center">{renderPlanValue(company)}</TableCell>
                        <TableCell style={cellStyle(company)} align="center">{renderStatus(company.status)}</TableCell>
                        <TableCell style={cellStyle(company)} align="center">
                          {dateToClient(company.dueDate)}
                          <br />
                          <small style={cellStyle(company)}>{company.recurrence}</small>
                        </TableCell>
                      </TableRow>
                    ))}
                    {records.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          Nenhuma empresa encontrada
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile View */}
              <div className={classes.mobileOnly}>
                {records.length === 0 ? (
                  <Box textAlign="center" py={3}>
                    <Typography color="textSecondary">
                      Nenhuma empresa encontrada
                    </Typography>
                  </Box>
                ) : (
                  records.map((company) => (
                    <Box
                      key={company.id}
                      className={classes.mobileRow}
                      style={rowStyle(company)}
                    >
                      <Typography
                        className={classes.mobileCompanyName}
                        style={cellStyle(company)}
                      >
                        {company.name}
                      </Typography>
                      {company.status ? (
                        <CheckCircleIcon
                          className={classes.statusIcon}
                          style={{ color: green[500] }}
                        />
                      ) : (
                        <CancelIcon
                          className={classes.statusIcon}
                          style={{ color: red[500] }}
                        />
                      )}
                    </Box>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </Paper>
    </Box>
  );
};

export default CompaniesList;